// src/components/AdminSignup.js
import React, { useState, useEffect, useRef } from "react";
import { Country, State, City } from "country-state-city";
import { useNavigate } from "react-router-dom";
import { FaTimes } from "react-icons/fa";
import { getAuth, signInWithEmailAndPassword, signOut } from "firebase/auth";
import { collection, getDocs, orderBy, query } from "firebase/firestore";
import { generateCompanyId } from "../utils/generalUtils";
import { db } from "../firebase/firebase";
import {
  checkEmailAvailability,
  submitAdminSignupRequest,
} from "../services/adminSignupService";

// Material-UI imports for modern alert display
import { Snackbar, Alert } from "@mui/material";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";

import "./AdminSignup.css";

// ─── Constants ────────────────────────────────────────────────────────────────

const INITIAL_FORM = {
  name: "",
  email: "",
  company: "",
  companyId: "",
  countryCode: "IN",
  stateCode: "",
  city: "",
  contactPersonMobile: "",
};

const MASTER_ADMIN_EMAIL =
  process.env.REACT_APP_MASTER_ADMIN_EMAIL || "masteradmin@company.com";
const MASTER_ADMIN_PASSWORD =
  process.env.REACT_APP_MASTER_ADMIN_PASSWORD || "Ast@Inf@2025";

console.log("Master Admin Email:", process.env.REACT_APP_MASTER_ADMIN_EMAIL);

// ─── Component ────────────────────────────────────────────────────────────────

const AdminSignup = () => {
  const [formData, setFormData] = useState(INITIAL_FORM);
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [checkingEmail, setCheckingEmail] = useState(false);
  const [loadingCities, setLoadingCities] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [authReady, setAuthReady] = useState(false);
  const [companiesList, setCompaniesList] = useState([]); // ← loaded once

  const didMasterLogin = useRef(false);
  const authReadyRef = useRef(false);

  const companyIdTimer = useRef(null);
  const emailTimer = useRef(null);

  const navigate = useNavigate();

  // ─── Silent Master-Admin Sign-in ─────────────────────────────────────────

  useEffect(() => {
    const auth = getAuth();

    if (auth.currentUser) {
      setAuthReady(true);
      authReadyRef.current = true;
      return;
    }

    signInWithEmailAndPassword(auth, MASTER_ADMIN_EMAIL, MASTER_ADMIN_PASSWORD)
      .then(() => {
        didMasterLogin.current = true;
        authReadyRef.current = true;
        setAuthReady(true);
      })
      .catch((err) => {
        console.error("[AdminSignup] Master admin sign-in failed:", err);
        setError("Unable to prepare signup. Please try again later.");
      });
  }, []);

  // Sign out on unmount ONLY if we performed the master login.
  useEffect(() => {
    return () => {
      if (!didMasterLogin.current) return;
      const auth = getAuth();
      if (auth.currentUser?.email === MASTER_ADMIN_EMAIL) {
        signOut(auth).catch(() => {});
      }
    };
  }, []);

  // ─── Load companies ONCE (after auth is ready) ───────────────────────────

  useEffect(() => {
    if (!authReady) return;
    let cancelled = false;

    (async () => {
      try {
        const q = query(
          collection(db, "companies"),
          orderBy("companyId", "desc"),
          // limit(500)   // optional safety cap
        );
        const snap = await getDocs(q);
        if (cancelled) return;
        setCompaniesList(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      } catch (err) {
        console.error("[AdminSignup] Failed to load companies:", err);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [authReady]);

  // ─── Geography Setup ─────────────────────────────────────────────────────

  useEffect(() => {
    setCountries(Country.getAllCountries());
    setStates(State.getStatesOfCountry("IN"));
  }, []);

  useEffect(() => {
    setStates(
      formData.countryCode
        ? State.getStatesOfCountry(formData.countryCode) || []
        : [],
    );
  }, [formData.countryCode]);

  useEffect(() => {
    if (formData.stateCode && formData.countryCode) {
      setLoadingCities(true);
      const result =
        City.getCitiesOfState(formData.countryCode, formData.stateCode) || [];
      setCities(result.sort((a, b) => a.name.localeCompare(b.name)));
      setLoadingCities(false);
    } else {
      setCities([]);
    }
  }, [formData.stateCode, formData.countryCode]);

  // ─── Cleanup timers ──────────────────────────────────────────────────────

  useEffect(() => {
    return () => {
      if (companyIdTimer.current) clearTimeout(companyIdTimer.current);
      if (emailTimer.current) clearTimeout(emailTimer.current);
    };
  }, []);

  // ─── Toast Auto-Dismiss ──────────────────────────────────────────────────

  useEffect(() => {
    if (!showToast) return;
    const timer = setTimeout(async () => {
      await safeExit();
    }, 5000);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showToast]);

  // ─── Safe exit ───────────────────────────────────────────────────────────

  const safeExit = async () => {
    try {
      const auth = getAuth();
      if (
        didMasterLogin.current &&
        auth.currentUser?.email === MASTER_ADMIN_EMAIL
      ) {
        await signOut(auth);
      }
    } catch (err) {
      console.warn("[AdminSignup] Sign-out failed:", err);
    }
    navigate("/");
  };

  // ─── Wait-for-auth helper (email check only) ─────────────────────────────

  const waitForAuth = () =>
    new Promise((resolve) => {
      if (authReadyRef.current) return resolve(true);
      const interval = setInterval(() => {
        if (authReadyRef.current) {
          clearInterval(interval);
          resolve(true);
        }
      }, 100);
      setTimeout(() => {
        clearInterval(interval);
        resolve(false);
      }, 8000);
    });

  // ─── Field Change Handler ────────────────────────────────────────────────

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (error) setError("");

    if (name === "countryCode") {
      setFormData((prev) => ({
        ...prev,
        countryCode: value,
        stateCode: "",
        city: "",
      }));
      return;
    }

    if (name === "stateCode") {
      setFormData((prev) => ({ ...prev, stateCode: value, city: "" }));
      return;
    }

    // ── Company name → synchronous, no Firestore, no await ──
    if (name === "company") {
      setFormData((prev) => ({ ...prev, company: value }));

      if (companyIdTimer.current) clearTimeout(companyIdTimer.current);

      companyIdTimer.current = setTimeout(() => {
        if (!value.trim()) {
          setFormData((prev) => ({ ...prev, companyId: "" }));
          return;
        }
        const newId = generateCompanyId(value, companiesList); // ← pure
        setFormData((prev) => {
          if (prev.company !== value) return prev;
          return { ...prev, companyId: newId };
        });
      }, 500);
      return;
    }

    // ── Email ──
    if (name === "email") {
      setFormData((prev) => ({ ...prev, email: value }));

      if (emailTimer.current) clearTimeout(emailTimer.current);

      const trimmed = value.trim();
      if (trimmed && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
        emailTimer.current = setTimeout(async () => {
          const ok = await waitForAuth();
          if (!ok) {
            console.warn("[AdminSignup] Auth not ready — skipping email check");
            return;
          }
          setCheckingEmail(true);
          try {
            const result = await checkEmailAvailability(trimmed);
            if (result.exists) {
              setError(
                `Email already exists in ${result.location}. Please use a different email.`,
              );
            }
          } catch (err) {
            console.warn("[AdminSignup] Email check failed:", err);
          } finally {
            setCheckingEmail(false);
          }
        }, 800);
      }
      return;
    }

    if (name === "contactPersonMobile") {
      setFormData((prev) => ({
        ...prev,
        [name]: value.replace(/[^0-9]/g, ""),
      }));
      return;
    }

    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // ─── Submit Handler ──────────────────────────────────────────────────────

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    const ok = await waitForAuth();
    if (!ok) {
      setError("Signup is not ready yet. Please try again in a moment.");
      return;
    }

    setSubmitting(true);

    const result = await submitAdminSignupRequest(formData);

    if (!result.success) {
      setError(result.error);
      setSubmitting(false);
      return;
    }

    setFormData(INITIAL_FORM);
    setShowToast(true);
    setSubmitting(false);
  };

  const closeToast = async () => {
    setShowToast(false);
    await safeExit();
  };

  // ─── Render ──────────────────────────────────────────────────────────────

  return (
    <>
      <div className="admin-signup-modal-overlay">
        <div className="admin-signup-modal-content">
          <div className="admin-signup-modal-header">
            <h3>Sign Up</h3>
            <button
              className="admin-signup-close-button"
              onClick={safeExit}
              aria-label="Close and return to home"
              type="button"
            >
              <FaTimes />
            </button>
          </div>

          <div className="admin-signup-modal-body">
            <form onSubmit={handleSubmit} noValidate>
              <div className="admin-signup-form-single-column">
                <div className="admin-signup-form-group">
                  <label htmlFor="name">Full Name *</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    maxLength="100"
                    placeholder="Enter your full name"
                    disabled={submitting}
                  />
                </div>

                <div className="admin-signup-form-group">
                  <label htmlFor="email">Email Address *</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    placeholder="Enter your professional email"
                    disabled={submitting}
                  />
                  {checkingEmail && (
                    <div className="admin-signup-form-helper">
                      <span className="admin-signup-spinner-small"></span>
                      Checking email availability...
                    </div>
                  )}
                </div>

                <div className="admin-signup-form-group">
                  <label htmlFor="company">Company Name *</label>
                  <input
                    type="text"
                    id="company"
                    name="company"
                    value={formData.company}
                    onChange={handleChange}
                    required
                    maxLength="100"
                    placeholder="Enter your company name"
                    disabled={submitting}
                    autoComplete="off"
                  />
                </div>
                {/*
                <div className="admin-signup-form-group">
                <label htmlFor="companyId">Company ID *</label> */}
                <input
                  type="hidden"
                  id="companyId"
                  name="companyId"
                  value={formData.companyId}
                  onChange={handleChange}
                  required
                  maxLength="20"
                  pattern="[a-zA-Z0-9\-\_]+"
                  placeholder="Auto-filled"
                  disabled={submitting}
                />
                {/*<div className="admin-signup-form-helper">
                    Auto-generated from company name (you can modify if needed)
                  </div>
                </div> */}

                <div className="admin-signup-form-group">
                  <label htmlFor="contactPersonMobile">
                    Contact Person Mobile *
                  </label>
                  <input
                    type="tel"
                    id="contactPersonMobile"
                    name="contactPersonMobile"
                    value={formData.contactPersonMobile}
                    onChange={handleChange}
                    required
                    maxLength="10"
                    placeholder="10-digit mobile number"
                    disabled={submitting}
                  />
                </div>

                <div className="admin-signup-form-group">
                  <label htmlFor="countryCode">Country *</label>
                  <select
                    id="countryCode"
                    name="countryCode"
                    value={formData.countryCode}
                    onChange={handleChange}
                    required
                    disabled={submitting}
                  >
                    <option value="">Select Country</option>
                    {countries.map((c) => (
                      <option key={c.isoCode} value={c.isoCode}>
                        {c.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="admin-signup-form-group">
                  <label htmlFor="stateCode">State *</label>
                  <select
                    id="stateCode"
                    name="stateCode"
                    value={formData.stateCode}
                    onChange={handleChange}
                    required
                    disabled={!formData.countryCode || submitting}
                  >
                    <option value="">
                      {!formData.countryCode
                        ? "Select Country First"
                        : "Select State"}
                    </option>
                    {states.map((s) => (
                      <option key={s.isoCode} value={s.isoCode}>
                        {s.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="admin-signup-form-group">
                  <label htmlFor="city">City *</label>
                  <select
                    id="city"
                    name="city"
                    value={formData.city}
                    onChange={handleChange}
                    required
                    disabled={
                      !formData.stateCode || loadingCities || submitting
                    }
                  >
                    <option value="">
                      {!formData.stateCode
                        ? "Select State First"
                        : loadingCities
                          ? "Loading cities..."
                          : "Select City"}
                    </option>
                    {cities.map((c, i) => (
                      <option key={`${c.name}-${i}`} value={c.name}>
                        {c.name}
                      </option>
                    ))}
                  </select>
                  {loadingCities && (
                    <div className="admin-signup-form-helper">
                      Loading cities...
                    </div>
                  )}
                </div>
              </div>

              <div className="admin-signup-modal-footer">
                <p className="admin-signup-footer-note">
                  By signing up, you agree that your request will be reviewed by
                  our team. You will receive an email confirmation once
                  submitted.
                </p>
                <div className="admin-signup-footer-buttons">
                  <button
                    type="button"
                    className="admin-signup-btn-secondary"
                    onClick={safeExit}
                    disabled={submitting}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="admin-signup-btn-primary"
                    disabled={submitting || checkingEmail}
                  >
                    {submitting ? (
                      <>
                        <span
                          className="admin-signup-spinner"
                          aria-hidden="true"
                        ></span>
                        Submitting Request...
                      </>
                    ) : (
                      "Submit"
                    )}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>

      {showToast && (
        <div className="admin-signup-toast-overlay">
          <div className="admin-signup-toast">
            <div className="admin-signup-toast-icon">✅</div>
            <div className="admin-signup-toast-content">
              <h4>Admin Request Submitted Successfully!</h4>
              <p>
                Your admin access request has been received. Our team will
                review your request and contact you via email within 1-2
                business days.
              </p>
              <p>You will receive a confirmation email shortly.</p>
            </div>
            <button
              className="admin-signup-toast-close"
              onClick={closeToast}
              aria-label="Close notification"
              type="button"
            >
              <FaTimes />
            </button>
          </div>
        </div>
      )}

      {/* ── ERROR SNACKBAR ────────────────────────────────────────────────── */}
      <Snackbar
        open={Boolean(error)}
        autoHideDuration={6000}
        onClose={() => setError("")}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        sx={{ mt: 8 }}
      >
        <Alert
          onClose={() => setError("")}
          severity="error"
          variant="filled"
          icon={<ErrorOutlineIcon />}
          sx={{
            width: "100%",
            minWidth: 400,
            fontSize: "0.9rem",
            fontWeight: 500,
          }}
        >
          {error}
        </Alert>
      </Snackbar>
    </>
  );
};

export default AdminSignup;